import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { Provider } from "react-redux";
import store from "./redux-store/store";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import { clerkFrontendKey, clerkLiveFrontendKey } from "./utils/constants";
import {
  init as sentryInit,
  reactRouterV6BrowserTracingIntegration,
  replayIntegration,
} from "@sentry/react";
import { Config } from "./config";
import { ClerkGate } from "./components/Wrappers/ClerkGate";

sentryInit({
  dsn: "https://15f8778257b65a1a4685427345eabcdf@o4507612352675840.ingest.us.sentry.io/4507855232630784",
  environment: import.meta.env.REACT_APP_STAGE || "local",
  // Set by CI to "dqpv2-wfe@<commit-sha>" to match the release the pipeline
  // creates via sentry-cli. Left undefined for local dev builds.
  release: import.meta.env.REACT_APP_SENTRY_RELEASE,
  integrations: [
    reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled.
  // Left at "localhost" only: the backend has no Sentry SDK (App Insights only), so it can't
  // consume sentry-trace/baggage headers, and attaching them to the real cross-origin API
  // gateway hosts would add headers the gateway's CORS policy has never allow-listed — risking
  // preflight failures on every API call for zero tracing benefit. Revisit if the backend ever
  // adds Sentry.
  tracePropagationTargets: ["localhost"],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// Benign browser message from ResizeObserver-heavy components (table/scrollbar
// mirroring). CRA's dev-only error overlay treats it as a crash; stop it here
// before the overlay's listener sees it. Harmless, and never fires in production.
window.addEventListener("error", (e) => {
  if (e.message === "ResizeObserver loop completed with undelivered notifications.") {
    e.stopImmediatePropagation();
  }
});

const root = ReactDOM.createRoot(document.getElementById("root"));

/**
 * QueryClient Configuration - Optimized for role switching
 *
 * Key settings to prevent API storm during role changes:
 * - refetchOnWindowFocus: false - Prevents refetch when switching tabs/windows
 * - refetchOnReconnect: false - Prevents refetch on network reconnection
 * - retry: false - Prevents retry storms during role transitions
 * - staleTime: 5 minutes - Caches data to avoid unnecessary refetches
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      staleTime: 1000 * 60 * 5, // 5 minutes - prevents refetch storm during role switch
    },
  },
});

const clerkKey =
  import.meta.env.REACT_APP_STAGE === "prod"
    ? clerkLiveFrontendKey
    : clerkFrontendKey;

root.render(
  <ClerkProvider
    publishableKey={clerkKey}
    allowedRedirectOrigins={[`${Config.apiGateway.HC_URL}`]}
  >
    <ClerkGate>
      <Provider store={store}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </Provider>
    </ClerkGate>
  </ClerkProvider>
);
